'use client';
import { useImpersonationOverlay } from '../impersonation/auth-overlay-context.js';
import { useAuthActions } from './auth-actions-context.js';
import { useMorfiAuth } from './use-morfi-auth.js';
// ── Helpers ───────────────────────────────────────────────────────────────────
function buildOverlaidUser(target) {
    const connectedProviders = target.connectedProviders.map((p) => {
        // `SdkConnectedProvider.username` and `.displayName` are optional (not nullable).
        // Drop nulls from the impersonation target — `undefined` means "not present".
        const entry = { provider: p.provider };
        const withUsername = p.username !== null ? { ...entry, username: p.username } : entry;
        const withAll = p.displayName !== null ? { ...withUsername, displayName: p.displayName } : withUsername;
        return withAll;
    });
    return {
        id: target.userId,
        email: '',
        phoneNumber: null,
        displayName: target.displayName,
        handle: target.handle,
        avatarUrl: target.avatarUrl,
        avatarNsfw: target.avatarNsfw,
        avatarPreferences: target.avatarPreferences ?? null,
        heroUrl: target.heroUrl,
        coverNsfw: target.heroNsfw,
        bio: null,
        country: null,
        locationSource: null,
        createdAt: new Date(0).toISOString(),
        onboardingCompleted: true,
        connectedProviders,
        turnkeySubOrgId: null,
    };
}
// ── Hook ──────────────────────────────────────────────────────────────────────
/**
 * Returns the current auth state, applying the engineer impersonation overlay
 * when active. The returned `logout` / `refreshSession` / `setUser` methods
 * always reflect the engineer's own session, never the impersonation target's.
 *
 * Drop-in replacement for the old `frontend/src/hooks/auth/use-auth.ts`.
 * Import from `@morfi/backend-sdk-v2/client`.
 */
export function useAuth() {
    const { status, user } = useMorfiAuth();
    const actions = useAuthActions();
    const { isActive: isImpersonating, target } = useImpersonationOverlay();
    const baseValue = {
        isAuthenticated: status === 'authed',
        isLoading: status === 'loading',
        user,
        error: null,
        ...actions,
    };
    if (!isImpersonating || target === null) {
        return baseValue;
    }
    const overlaidUser = buildOverlaidUser(target);
    return {
        ...baseValue,
        isAuthenticated: true,
        isLoading: false,
        user: overlaidUser,
    };
}
//# sourceMappingURL=use-auth.js.map